import React, { useEffect, useRef, useState } from "react";
import PerfectScrollbar from "perfect-scrollbar";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import ButtonAppBar from "components/Navbars/navBar";
import SidebarAdmin from "../../components/Sidebar/Sidebaradmin";
import useAdminRoutes from "../../views/admin/adminRoutes";
import AdminProfile from "../../views/admin/adminProfile";
import CompanyProfile from "../../views/admin/companyProfile";
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import Navbar from "components/Navbars/nav";
import Footer from "components/Footer/Footer.js";
import CreateRecruiter from "../../views/admin/createRecruiter";
import styled from "styled-components";
import useFetchRecruiter from "hooks/useFetchRecruiter";

const MainContent = styled.div`
  overflow-y: auto;
  height: calc(100vh - 60px);
  
  transition: margin-left 0.3s ease;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  position: relative;
  min-height: 100vh;
`;

const MainPanel = styled.div`
  flex: 1;
  position: relative;
  transition: margin-left 0.3s ease;
  margin-left: ${({ $isSidebarCollapsed }) => 
    $isSidebarCollapsed ? "80px" : "270px"};
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const NonTransparentNavWrapper = styled.div`
  .navbar {
    background-color: #ffffff !important;
    backdrop-filter: none !important;
    opacity: 1 !important;
  }
`;

function AdminA(props) {
  const [backgroundColor, setBackgroundColor] = useState("black");
  const [activeColor, setActiveColor] = useState("info");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const mainPanel = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [currentTitle, setCurrentTitle] = useState('');
  const [adminData, setAdminData] = useState({
    company: "",
    email: "",
    first_name: "",
    last_name: "",
    company_logo: "",
  });
  
  // Use the admin routes hook
  const adminRoutes = useAdminRoutes();
  
  let ps;
  useEffect(() => {
    checkUserRole();
    fetchAdminData();
  }, []);

  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  };

  const fetchAdminData = async () => {
    const userId = getUserIdFromAccessToken();
    if (!userId) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}authentication/admins/${userId}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAdminData({
          company: data.company || "",
          email: data.email || "",
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          company_logo: data.image || "",
          created_by: {
            company_logo: data.image || ""
          }
        });
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
    }
  };

  const updateAdminData = (updatedData) => {
    console.log('🔄 Updating navbar with new admin data');
    setAdminData(prevData => {
      const newData = {
        ...prevData,
        ...updatedData,
        company_logo: updatedData.image || updatedData.company_logo || prevData.company_logo,
        created_by: {
          company_logo: updatedData.image || updatedData.company_logo || prevData.company_logo
        }
      };
      console.log('✅ Navbar data updated successfully');
      return newData;
    });
  };

  useEffect(() => {
    // Update page title based on current route
    const matchedRoute = adminRoutes.find((route) => {
      const fullPath = `${route.layout}${route.path}`;
      console.log('🔍 Checking route:', fullPath);
      return location.pathname === fullPath || location.pathname.startsWith(fullPath + '/');
    });
    console.log('🔄 Updating page title to2:', matchedRoute);
    if (matchedRoute) {
      setCurrentTitle(matchedRoute.name);
    } else {
      console.log('🔄 No matching route found');
      setCurrentTitle('');
    }
  }, [location.pathname, adminRoutes]);

  const checkUserRole = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      // No access token found, redirect to login
      navigate("/login");
      return;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      if (decodedToken.role !== "a") {
        // User is not an AdminA, redirect to login
        navigate("/login");
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      navigate("/login");
    }
  };

  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1 && mainPanel.current) {
      ps = new PerfectScrollbar(mainPanel.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
      document.body.classList.toggle("perfect-scrollbar-on");
    }
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1 && ps) {
        ps.destroy();
        document.body.classList.toggle("perfect-scrollbar-on");
      }
    };
  }, []);

  useEffect(() => {
    if (mainPanel.current) {
      mainPanel.current.scrollTop = 0;
    }
    document.scrollingElement.scrollTop = 0;
  }, [location]);

  const handleActiveClick = (color) => {
    setActiveColor(color);
  };

  const handleBgClick = (color) => {
    setBackgroundColor(color);
  };

  const handleToggleSidebar = (newCollapsedState) => {
    setIsSidebarCollapsed(newCollapsedState);
  };

  const handleMenuClick = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  return (
    <Wrapper>
      <SidebarAdmin
        {...props}
        routes={adminRoutes}
        bgColor={backgroundColor}
        activeColor={activeColor}
        isSidebarCollapsed={isSidebarCollapsed}
        isMobile={isMobileSidebarOpen}
        onClose={() => setIsMobileSidebarOpen(false)}
        onOpen={() => setIsMobileSidebarOpen(true)}
        onToggleSidebar={handleToggleSidebar}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
      />
      <MainPanel $isSidebarCollapsed={isSidebarCollapsed} ref={mainPanel}>
        <NonTransparentNavWrapper>
          <Navbar 
            formData={adminData} 
            title={currentTitle} 
            onMenuClick={handleMenuClick}
          />
        </NonTransparentNavWrapper>
        <MainContent $isSidebarCollapsed={isSidebarCollapsed}>
          <Routes>
            {adminRoutes.map((prop, key) => {
              // Special handling for AdminProfile and CompanyProfile to pass updateAdminData prop
              if (prop.path === "/AdminProfile") {
                return (
                  <Route
                    path={prop.path}
                    element={<AdminProfile updateAdminData={updateAdminData} />}
                    key={key}
                    exact
                  />
                );
              }
              if (prop.path === "/AdminCompanyProfile") {
                return (
                  <Route
                    path={prop.path}
                    element={<CompanyProfile />}
                    key={key}
                    exact
                  />
                );
              }
              return (
                <Route
                  path={prop.path}
                  element={prop.component}
                  key={key}
                  exact
                />
              );
            })}
            <Route path="/createRecruiter" element={<CreateRecruiter />} />
          </Routes>
          {/* <Footer /> */}
        </MainContent>
      </MainPanel>
    </Wrapper>
  );
}

export default AdminA;